import { Box, Stack, Typography } from '@mui/material';
import { first } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ConfirmationModal } from '@/components/modals/ConfirmationModal/ConfirmationModal';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { Loader } from '@/components/progress/Loader/Loader';
import { useModalState } from '@/hooks/useModalState';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { canonicalizeSummaryForDirtyCheck } from '@/modules/documents/documents.utils';
import {
  isEntityNotesEnabled,
  makeGenerateEntityNotesFromDocumentSummaryInput,
} from '@/modules/entities/components/EntityNotesPerDeath/EntityNotesPerDeath.util';
import { GenerateAINotesModal } from '@/modules/entities/components/EntityNotesPerDeath/GenerateAINotesModal';
import { useGenerateEntityNotesFromDocumentSummaryMutation } from '@/modules/entities/components/EntityNotesPerDeath/graphql/GenerateEntityNotesFromDocumentSummary.generated';
import { Operations as GetHouseholdDocumentsOperations } from '@/modules/entities/components/EntityNotesPerDeath/graphql/GetHouseholdDocuments.generated';
import { Operations } from '@/modules/entities/details/graphql/EntityDetailPage.generated';
import { isEntityTimeBound } from '@/modules/entities/utils/getEntityNotesForDeath';
import { useUpdateContextData } from '@/modules/presentation/clientPresentation/ClientPresentationDesigner.hooks';
import { usePresentationEditingContext } from '@/modules/presentation/context/PresentationEditing.context';
import { SlideEditorRichTextEditor } from '@/modules/slideEditor/textEditors/SlideEditor.richTextEditor';
import { COLORS } from '@/styles/tokens/colors';
import { diagnostics } from '@/utils/diagnostics';

import { EntityNotesToolbarButtons } from './EntityNotesToolbarButtons';
import { EntityDiagramAndNotesSlide_EntityFragment } from './graphql/EntityDiagramAndNotesSlide.generated';

export interface EditableNotesDisplayProps {
  displayNotes: string;
  onSubmit: (newNotes: string) => void | Promise<void>;
  entity: EntityDiagramAndNotesSlide_EntityFragment;
  deathOrderId: string | null | undefined;
}

export function EditableNotesDisplay({
  displayNotes,
  onSubmit: onSubmitExternal,
  entity,
  deathOrderId,
}: EditableNotesDisplayProps) {
  const trackUserEvent = useTrackUserEvent();
  const { updateContextData } = useUpdateContextData();
  // Outside the designer (the single-entity export preview), refresh via the
  // surface's own primitive; the designer's context-data updater is inert there.
  const editing = usePresentationEditingContext();
  const { showFeedback } = useFeedback();

  // Draft content updated on every keystroke via the editor's onRawUpdate.
  // Only persisted to the backend when the user clicks Save.
  const draftContent = useRef<string>(displayNotes);
  // Optimistic display notes: after save, immediately show the saved draft
  // so the editor doesn't flash back to the stale server value while the
  // Apollo cache updates.
  const [optimisticNotes, setOptimisticNotes] = useState<string | null>(null);
  useEffect(() => {
    draftContent.current = displayNotes;
    // Clear the optimistic override once the server value catches up
    setOptimisticNotes(null);
  }, [displayNotes]);

  const effectiveNotes = optimisticNotes ?? displayNotes;

  // Bumping this key remounts the editor, exiting edit mode and resetting
  // the editor's initial value to the current persisted content.
  const [editorVersion, setEditorVersion] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  const handleEditorUpdate = useCallback((str: string) => {
    draftContent.current = str;
  }, []);

  const handleSave = useCallback(async () => {
    const savedContent = draftContent.current;
    // Optimistically show the saved content immediately so there's no
    // flash of stale data while the server round-trip completes.
    setOptimisticNotes(savedContent);
    setEditorVersion((v) => v + 1);
    setIsSaving(true);
    try {
      await onSubmitExternal(savedContent);
    } catch {
      // Roll back optimistic update so the editor reverts to the last
      // persisted value on failure.
      setOptimisticNotes(null);
      draftContent.current = displayNotes;
      setEditorVersion((v) => v + 1);
    } finally {
      setIsSaving(false);
    }
  }, [onSubmitExternal, displayNotes]);

  const handleCancel = useCallback(() => {
    draftContent.current = displayNotes;
    setOptimisticNotes(null);
    setEditorVersion((v) => v + 1);
  }, [displayNotes]);

  // Unsaved changes confirmation when clicking away from the editor
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] =
    useState(false);

  const isDirty = useCallback(() => {
    const normalizedDraft = canonicalizeSummaryForDirtyCheck(
      draftContent.current
    );
    const normalizedOriginal = canonicalizeSummaryForDirtyCheck(displayNotes);
    return normalizedDraft !== normalizedOriginal;
  }, [displayNotes]);

  const handleClickAway = useCallback(() => {
    if (isDirty()) {
      setIsUnsavedChangesModalOpen(true);
    } else {
      // No changes — just close the editor
      setEditorVersion((v) => v + 1);
    }
  }, [isDirty]);

  const handleUnsavedChangesClose = useCallback(
    (reason: 'confirm' | 'cancel') => {
      setIsUnsavedChangesModalOpen(false);
      if (reason === 'confirm') {
        void handleSave();
      } else {
        handleCancel();
      }
    },
    [handleSave, handleCancel]
  );

  // AI generation
  const [generateEntityNotesFromDocumentSummary, { loading: isGenerating }] =
    useGenerateEntityNotesFromDocumentSummaryMutation();
  const isTimeBoundEntity = isEntityTimeBound(entity);
  const [
    { isModalOpen: isGenerateAiNotesModalOpen },
    {
      openModal: openGenerateAiNotesModal,
      closeModal: closeGenerateAiNotesModal,
    },
  ] = useModalState();

  const handleGenerate = useCallback(
    async (documentSummaryId: string) => {
      trackUserEvent('entity_notes generate clicked');
      closeGenerateAiNotesModal();
      await generateEntityNotesFromDocumentSummary({
        refetchQueries: [
          Operations.Query.EntityDetails,
          GetHouseholdDocumentsOperations.Query.GetHouseholdDocuments,
        ],
        notifyOnNetworkStatusChange: true,
        variables: {
          input: makeGenerateEntityNotesFromDocumentSummaryInput({
            documentSummaryId,
            entityId: entity.id,
            householdId: entity.household.id,
            activeDeathOrderId: deathOrderId,
            isTimeBoundEntity,
            syncDeathOrderNotes: entity.syncDeathOrderNotes,
          }),
        },
        onCompleted: (data) => {
          const firstNote = first(
            data.generateEntityNotesFromDocumentSummary
          )?.notes;
          if (firstNote) {
            draftContent.current = firstNote;
            // Optimistically show the generated content before the refetch
            // updates displayNotes, same pattern as handleSave.
            setOptimisticNotes(firstNote);
          }
          showFeedback('Notes updated', { variant: 'success' });
          if (editing) {
            editing.onRefresh();
          } else {
            void updateContextData();
          }
          // Remount the editor so it picks up the generated content
          setEditorVersion((v) => v + 1);
        },
        onError: (err) => {
          diagnostics.error(
            'Failed to create AI generated notes from document summary',
            err
          );
          showFeedback(
            'Failed to create AI generated notes from document summary'
          );
        },
      });
    },
    [
      closeGenerateAiNotesModal,
      deathOrderId,
      editing,
      entity,
      generateEntityNotesFromDocumentSummary,
      isTimeBoundEntity,
      showFeedback,
      trackUserEvent,
      updateContextData,
    ]
  );

  const showGenerateAI = isEntityNotesEnabled(entity.kind);

  const endToolbarButtons = (
    <EntityNotesToolbarButtons
      onGenerateAI={showGenerateAI ? openGenerateAiNotesModal : undefined}
      isGenerating={isGenerating}
      isSaving={isSaving}
      onSave={handleSave}
      onCancel={handleCancel}
    />
  );

  const hasContent = !!effectiveNotes;
  const isLoading = isGenerating || isSaving;

  return (
    <Box
      data-testid="editable-notes-container"
      sx={{
        pointerEvents: 'auto',
        width: '100%',
        height: '100%',
        display: 'flex',
        border: hasContent
          ? `1px solid ${COLORS.GRAY[200]}`
          : `1px dashed ${COLORS.GRAY[500]}`,
        borderRadius: '4px',
        p: 2,
        // Constrain the editor content so it scrolls within the slide bounds
        // while the toolbar remains fixed above it.
        overflow: 'hidden',
      }}
    >
      {isLoading ? (
        <Stack
          sx={{ width: '100%' }}
          alignItems="center"
          justifyContent="center"
          direction="column"
        >
          <Stack alignItems="center" justifyContent="center">
            <Loader circularProgressProps={{ size: 20 }} />
            <Typography variant="title-xl" color={COLORS.TEXT.SECONDARY}>
              {isGenerating ? 'Generating notes' : 'Saving notes'}
            </Typography>
            {isGenerating && (
              <Typography variant="body-xs">
                This may take up to 10 seconds
              </Typography>
            )}
          </Stack>
        </Stack>
      ) : (
        <SlideEditorRichTextEditor
          key={editorVersion}
          content={effectiveNotes}
          onRawUpdate={handleEditorUpdate}
          endToolbarButtons={endToolbarButtons}
          disableHover
          onClickAway={handleClickAway}
          emptyState={
            <Stack
              alignItems="center"
              justifyContent="center"
              width="100%"
              height="100%"
              gap={2}
            >
              <Stack alignItems="center">
                <Typography variant="body-xs" sx={{ fontWeight: 'bold' }}>
                  Click to add a custom description to this section*
                </Typography>
                <Typography variant="body-xs">
                  If no notes are added the diagram will fill the page when
                  exported
                </Typography>
              </Stack>
              <Typography variant="title-xs">
                *Any edits will be synced to the entity
              </Typography>
            </Stack>
          }
        />
      )}
      {/* Rendered outside the toolbar so the modal's backdrop sits above the
          toolbar Popper (which is at zIndexes.POPOVER). */}
      {showGenerateAI && (
        <GenerateAINotesModal
          householdId={entity.household.id}
          entityId={entity.id}
          entityKind={entity.kind}
          isOpen={isGenerateAiNotesModalOpen}
          handleClose={closeGenerateAiNotesModal}
          handleGenerate={handleGenerate}
          isGenerating={isGenerating}
        />
      )}
      {isUnsavedChangesModalOpen && (
        <ConfirmationModal
          isOpen
          heading="Unsaved changes"
          confirmText="Save"
          cancelText="Discard changes"
          onClose={handleUnsavedChangesClose}
        >
          You have unsaved changes. Would you like to save or discard them?
        </ConfirmationModal>
      )}
    </Box>
  );
}
